import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { QualificationSummary } from '../models/qualificationSummary.model';
import { QualificationSummaryService } from '../services/qualificationSummaryService';

interface UseQualificationSummaryProps {
  personId: number;
  initialSummaries: QualificationSummary[];
  onError?: () => Promise<void>;
}

export function useQualificationSummary({
  personId,
  initialSummaries,
  onError
}: UseQualificationSummaryProps) {
  const [summaries, setSummaries] = useState<QualificationSummary[]>(initialSummaries);
  const [isLoading, setIsLoading] = useState(false);
  const summaryService = new QualificationSummaryService();

  // Update internal state when initialSummaries change (from parent)
  useEffect(() => {
    if (initialSummaries && initialSummaries.length > 0 && summaries.length === 0) {
      console.log('Setting initial summaries in hook:', initialSummaries);
      setSummaries(initialSummaries);
    }
  }, [initialSummaries]);

  const updateSummaries = async (newSummaries: QualificationSummary[]) => {
    if (personId <= 0) return;
    
    setIsLoading(true);

    try {
      // Update local state immediately for responsive UI
      setSummaries(newSummaries);

      const currentSummaries = summaries || [];
      const hadNoSummariesBefore = currentSummaries.length === 0;

      // Find which summary is marked as selected or use first one if this is user's first summary
      let selectedSummary = newSummaries.find(summary => summary.selectedForCv);

      // If no summary is explicitly selected AND this is the first summary being added,
      // select the first one automatically
      if (!selectedSummary && hadNoSummariesBefore && newSummaries.length > 0) {
        selectedSummary = newSummaries[0];
        // Update local state to show it's selected
        newSummaries[0].selectedForCv = true;
        setSummaries([...newSummaries]);
      }

      console.log('Processing summaries updates, selected summary:', selectedSummary?.id);

      // Process added/updated summaries
      for (const summary of newSummaries) {
        const summaryPayload = {
          id: summary.id,
          personId,
          name: summary.name,
          description: summary.description,
          selectedForCv: summary.selectedForCv
        };

        // Determine if this is a new summary
        const isNewSummary = summary.id === 0;

        if (isNewSummary) {
          // Create new summary
          console.log('Creating new summary:', summaryPayload);
          const addedSummary = await summaryService.addSummary(summaryPayload);
          console.log('Created summary with ID:', addedSummary.id);

          // If this was selected AND it was new, we need to update the selection with the new ID
          if (selectedSummary === summary) {
            selectedSummary = { ...selectedSummary, id: addedSummary.id };
          }
        } else {
          // Only call updateSummary if content (name/description) actually changed.
          // selectedForCv changes are handled exclusively by the selectSummaryForCV call below.
          const original = currentSummaries.find(s => s.id === summary.id);
          const contentChanged = !original || original.name !== summary.name || original.description !== summary.description;
          if (contentChanged) {
            console.log('Updating summary:', summaryPayload.id);
            await summaryService.updateSummary(summaryPayload);
          } else {
            console.log('Skipping updateSummary (content unchanged):', summaryPayload.id);
          }
        }
      }

      // Process deletions
      const deletedSummaries = currentSummaries.filter(
        currentSummary => !newSummaries.some(updatedSummary => updatedSummary.id === currentSummary.id)
      );

      for (const deletedSummary of deletedSummaries) {
        if (deletedSummary.id !== 0) {
          console.log('Deleting summary:', deletedSummary.id);
          await summaryService.deleteSummary(deletedSummary.id);
        }
      }

      // Only call selectForCv if the selected summary actually changed.
      const previouslySelected = currentSummaries.find(s => s.selectedForCv);
      const selectionChanged = selectedSummary?.id !== previouslySelected?.id;
      if (selectedSummary && selectedSummary.id !== 0 && selectionChanged) {
        console.log('Setting selected summary via selectSummaryForCV API call:', selectedSummary.id);
        await summaryService.selectSummaryForCV(selectedSummary.id, personId);
      }

      // Success notification and refresh data
      notifications.show({
        title: 'Success',
        message: 'Qualification summaries updated successfully',
        color: 'green'
      });

      // Refresh summaries from server to ensure we have the latest state
      console.log('Refreshing summaries after update');
      await refreshSummaries();

    } catch (error) {
      console.error("Error updating summaries:", error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update qualification summaries. Please try again.',
        color: 'red'
      });

      // On error, reload all data
      if (onError) {
        await onError();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSummaries = async () => {
    if (personId <= 0) return;
    
    console.log('Refreshing summaries for personId:', personId);
    setIsLoading(true);
    try {
      const updatedSummaries = await summaryService.getSummariesByPersonId(personId);
      console.log('Raw summaries from API:', JSON.stringify(updatedSummaries));

      // Ensure there's at least one summary selected if there are any summaries
      let hasSelectedSummary = false;

      const mappedSummaries = updatedSummaries.map((summary: any) => {
        console.log('Processing summary:', summary.id, 'selectedForCv:', summary.selectedForCv);

        // Check if this summary is selected
        if (summary.selectedForCv === true) {
          hasSelectedSummary = true;
        }

        return {
          id: summary.id,
          personId: summary.personId || 0,
          name: summary.name || '',
          description: summary.description || '',
          selectedForCv: summary.selectedForCv === true
        };
      });

      console.log('Mapped summaries:', mappedSummaries);

      // If no summary is selected but there are summaries, select the first one
      if (!hasSelectedSummary && mappedSummaries.length > 0) {
        console.log('No summary was selected, selecting the first one');
        const firstSummary = mappedSummaries[0];

        // Mark it as selected in local state
        firstSummary.selectedForCv = true;

        // Also update on backend
        try {
          await summaryService.selectSummaryForCV(firstSummary.id, personId);
          console.log('Selected summary on backend:', firstSummary.id);
        } catch (err) {
          console.error('Failed to select summary on backend:', err);
        }
      }

      setSummaries(mappedSummaries);
    } catch (error) {
      console.error("Error refreshing summaries:", error);
      notifications.show({
        title: 'Error',
        message: 'Failed to refresh qualification summaries',
        color: 'red'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    summaries,
    isLoading,
    updateSummaries,
    refreshSummaries
  };
} 